import React, { useEffect, useState } from "react";
import {
  Dialog,
  DialogContent,
  Box,
  Typography,
  TextField,
  MenuItem,
  Button,
  IconButton,
} from "@mui/material";

import CloseIcon from "@mui/icons-material/Close";
import { LocalizationProvider } from "@mui/x-date-pickers/LocalizationProvider";
import { AdapterDayjs } from "@mui/x-date-pickers/AdapterDayjs";
import { TimePicker } from "@mui/x-date-pickers/TimePicker";
import dayjs from "dayjs";
import customParseFormat from "dayjs/plugin/customParseFormat";
import utc from "dayjs/plugin/utc";

dayjs.extend(customParseFormat);
dayjs.extend(utc);

const eventTypes = [
  "Town Hall",
  "Canvass",
  "Phone Bank",
  "Fundraiser",
  "Meet & Greet",
  "Other",
];
const buttonLabels = ["Join Us", "RSVP Now", "Register"];
const statuses = ["upcoming", "completed", "cancelled"];
const todayDate = () => dayjs().format("YYYY-MM-DD");

const isPastDate = (value) => {
  if (!value) {
    return false;
  }

  const parsedDate = dayjs(value, "YYYY-MM-DD", true);
  return parsedDate.isValid() && parsedDate.isBefore(dayjs(), "day");
};

const getInitialFormData = (eventData) => {
  let initialTime = null;
  if (eventData?.startTime && eventData.startTime !== "-") {
    // Stored time is UTC — parse directly without timezone conversion
    const parsed = dayjs(eventData.startTime, ["HH:mm", "hh:mm A", "h:mm A"]);
    if (parsed.isValid()) {
      initialTime = parsed;
    }
  }

  return {
    title: eventData?.title || "",
    eventType: eventData?.type || "Town Hall",
    ctaLabel: eventData?.buttonLabel || "Join Us",
    status: eventData?.rawStatus || "upcoming",
    eventDate: eventData?.rawEventDate || "",
    eventTime: initialTime,
    duration: eventData?.duration === "-" ? "" : eventData?.duration || "",
    capacity: eventData?.capacity === "-" ? "" : eventData?.capacity || "",
    venueName: eventData?.venueName || "",
    address: eventData?.streetAddress || "",
    city: eventData?.city || "",
    zipCode: eventData?.zipCode || "",
    description:
      eventData?.description === "-" ? "" : eventData?.description || "",
    parkingInfo: eventData?.parkingInfo || "",
    accessibilityInfo: eventData?.accessibilityInfo || "",
    imageUrl: eventData?.imageUrl || "",
  };
};

const CreateEditEventModal = ({
  open,
  handleClose,
  eventData,
  onSave,
  isSaving = false,
}) => {
  const isEdit = Boolean(eventData);
  const [formData, setFormData] = useState(getInitialFormData(eventData));
  const [errors, setErrors] = useState({});

  useEffect(() => {
    if (open) {
      setFormData(getInitialFormData(eventData));
      setErrors({});
    }
  }, [eventData, open]);

  const handleChange = (field, value) => {
    setFormData((prev) => ({
      ...prev,
      [field]: value,
    }));

    // Clear error when user types
    if (errors[field]) {
      setErrors((prev) => ({
        ...prev,
        [field]: "",
      }));
    }
  };

  const handleNumericChange = (field, value) => {
    // Only allow numbers
    if (value === "" || /^\d+$/.test(value)) {
      handleChange(field, value);
    }
  };

  const handleZipCodeChange = (value) => {
    if (/^\d{0,6}$/.test(value)) {
      handleChange("zipCode", value);
    }
  };

  const handleStatusChange = (value) => {
    setFormData((prev) => ({
      ...prev,
      status: value,
      eventDate:
        value === "upcoming" && isPastDate(prev.eventDate)
          ? ""
          : prev.eventDate,
    }));

    if (errors.status || errors.eventDate) {
      setErrors((prev) => ({
        ...prev,
        status: "",
        eventDate: "",
      }));
    }
  };

  const handleEventDateChange = (value) => {
    if (formData.status === "upcoming" && isPastDate(value)) {
      setErrors((prev) => ({
        ...prev,
        eventDate: "Upcoming events cannot use a past date",
      }));
      return;
    }

    handleChange("eventDate", value);
  };

  const validate = () => {
    const newErrors = {};
    const requiredFields = [
      "title",
      "eventType",
      "eventDate",
      "eventTime",
      "venueName",
      "address",
      "city",
      "zipCode",
      "description",
    ];

    requiredFields.forEach((field) => {
      if (!formData[field]) {
        newErrors[field] = "This field is required";
      }
    });

    if (formData.eventTime && !dayjs(formData.eventTime).isValid()) {
      newErrors.eventTime = "Invalid time";
    }

    if (formData.status === "upcoming" && isPastDate(formData.eventDate)) {
      newErrors.eventDate = "Upcoming events cannot use a past date";
    }

    if (formData.zipCode && !/^\d{1,6}$/.test(formData.zipCode)) {
      newErrors.zipCode = "Zip code must be 6 digits or less";
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = async () => {
    if (!validate()) {
      return;
    }

    if (!onSave) {
      return;
    }

    await onSave({
      ...formData,
      eventTime: formData.eventTime
        ? dayjs(formData.eventTime).format("hh:mm A")
        : "",
      duration: formData.duration ? Number(formData.duration) : 0,
      capacity: formData.capacity ? Number(formData.capacity) : 0,
    });
  };

  return (
    <LocalizationProvider dateAdapter={AdapterDayjs}>
      <Dialog
        open={open}
        onClose={isSaving ? undefined : handleClose}
        maxWidth="sm"
        fullWidth
        PaperProps={{
          sx: {
            borderRadius: "8px",
            overflow: "hidden",
          },
        }}
      >
        <Box
          sx={{
            bgcolor: "#081B44",
            color: "#fff",
            px: 3,
            py: 2,
            display: "flex",
            justifyContent: "space-between",
            alignItems: "center",
          }}
        >
          <Typography sx={{ fontWeight: 700, fontSize: "22px" }}>
            {isEdit ? "Edit Event" : "New Event"}
          </Typography>

          <IconButton
            onClick={handleClose}
            disabled={isSaving}
            sx={{
              color: "#fff",
              border: "1px solid rgba(255,255,255,0.4)",
              width: 34,
              height: 34,
            }}
          >
            <CloseIcon fontSize="small" />
          </IconButton>
        </Box>

        <DialogContent sx={{ p: 3 }}>
          <Box sx={{ mb: 2.5 }}>
            <Typography sx={labelStyle}>TITLE *</Typography>
            <TextField
              fullWidth
              size="small"
              value={formData.title}
              onChange={(e) => handleChange("title", e.target.value)}
              error={Boolean(errors.title)}
              helperText={errors.title}
            />
          </Box>

          <Box sx={{ display: "flex", gap: 2, mb: 2.5, flexWrap: "wrap" }}>
            <Box sx={{ flex: 1, minWidth: "280px" }}>
              <Typography sx={labelStyle}>EVENT TYPE *</Typography>
              <TextField
                select
                fullWidth
                size="small"
                value={formData.eventType}
                onChange={(e) => handleChange("eventType", e.target.value)}
                error={Boolean(errors.eventType)}
                helperText={errors.eventType}
              >
                {eventTypes.map((item) => (
                  <MenuItem key={item} value={item}>
                    {item}
                  </MenuItem>
                ))}
              </TextField>
            </Box>

            <Box sx={{ flex: 1, minWidth: "280px" }}>
              <Typography sx={labelStyle}>BUTTON LABEL</Typography>
              <TextField
                select
                fullWidth
                size="small"
                value={formData.ctaLabel}
                onChange={(e) => handleChange("ctaLabel", e.target.value)}
              >
                {buttonLabels.map((item) => (
                  <MenuItem key={item} value={item}>
                    {item}
                  </MenuItem>
                ))}
              </TextField>
              <Typography sx={{ fontSize: "11px", color: "#999", mt: 0.5 }}>
                Text that appears on the event card button
              </Typography>
            </Box>
          </Box>

          <Box sx={{ display: "flex", gap: 2, mb: 2.5, flexWrap: "wrap" }}>
            <Box sx={{ flex: 1, minWidth: "280px" }}>
              <Typography sx={labelStyle}>STATUS</Typography>
              <TextField
                select
                fullWidth
                size="small"
                value={formData.status}
                onChange={(e) => handleStatusChange(e.target.value)}
              >
                {statuses.map((item) => (
                  <MenuItem key={item} value={item}>
                    {item.charAt(0).toUpperCase() + item.slice(1)}
                  </MenuItem>
                ))}
              </TextField>
            </Box>

            <Box sx={{ flex: 1, minWidth: "280px" }}>
              <Typography sx={labelStyle}>EVENT DATE *</Typography>
              <TextField
                fullWidth
                size="small"
                type="date"
                value={formData.eventDate}
                onChange={(e) => handleEventDateChange(e.target.value)}
                error={Boolean(errors.eventDate)}
                helperText={errors.eventDate}
                slotProps={{
                  htmlInput: {
                    min:
                      formData.status === "upcoming" ? todayDate() : undefined,
                  },
                }}
              />
            </Box>
          </Box>

          <Box sx={{ display: "flex", gap: 2, mb: 2.5, flexWrap: "wrap" }}>
            <Box sx={{ flex: 1, minWidth: "280px" }}>
              <Typography sx={labelStyle}>START TIME *</Typography>
              <TimePicker
                value={formData.eventTime}
                onChange={(newValue) => handleChange("eventTime", newValue)}
                slotProps={{
                  textField: {
                    fullWidth: true,
                    size: "small",
                    error: Boolean(errors.eventTime),
                    helperText:
                      errors.eventTime ||
                      "Enter the event time",
                  },
                }}
              />
            </Box>

            <Box sx={{ flex: 1, minWidth: "280px" }}>
              <Typography sx={labelStyle}>DURATION (MINUTES)</Typography>
              <TextField
                fullWidth
                size="small"
                value={formData.duration}
                onChange={(e) =>
                  handleNumericChange("duration", e.target.value)
                }
              />
            </Box>
          </Box>

          <Box sx={{ mb: 2.5 }}>
            <Typography sx={labelStyle}>CAPACITY</Typography>
            <TextField
              fullWidth
              size="small"
              value={formData.capacity}
              onChange={(e) => handleNumericChange("capacity", e.target.value)}
              error={Boolean(errors.capacity)}
              helperText={errors.capacity}
            />
          </Box>

          <Box sx={{ mb: 2.5 }}>
            <Typography sx={labelStyle}>VENUE NAME *</Typography>
            <TextField
              fullWidth
              size="small"
              value={formData.venueName}
              onChange={(e) => handleChange("venueName", e.target.value)}
              error={Boolean(errors.venueName)}
              helperText={errors.venueName}
            />
          </Box>

          <Box sx={{ mb: 2.5 }}>
            <Typography sx={labelStyle}>STREET ADDRESS *</Typography>
            <TextField
              fullWidth
              size="small"
              value={formData.address}
              onChange={(e) => handleChange("address", e.target.value)}
              error={Boolean(errors.address)}
              helperText={errors.address}
            />
          </Box>

          <Box sx={{ display: "flex", gap: 2, mb: 2.5, flexWrap: "wrap" }}>
            <Box sx={{ flex: 1, minWidth: "280px" }}>
              <Typography sx={labelStyle}>CITY *</Typography>
              <TextField
                fullWidth
                size="small"
                value={formData.city}
                onChange={(e) => handleChange("city", e.target.value)}
                error={Boolean(errors.city)}
                helperText={errors.city}
              />
            </Box>

            <Box sx={{ flex: 1, minWidth: "280px" }}>
              <Typography sx={labelStyle}>ZIP CODE *</Typography>
              <TextField
                fullWidth
                size="small"
                value={formData.zipCode}
                onChange={(e) => handleZipCodeChange(e.target.value)}
                error={Boolean(errors.zipCode)}
                helperText={errors.zipCode}
                slotProps={{
                  htmlInput: {
                    inputMode: "numeric",
                    maxLength: 6,
                    pattern: "[0-9]*",
                  },
                }}
              />
            </Box>
          </Box>

          <Box sx={{ mb: 2.5 }}>
            <Typography sx={labelStyle}>DESCRIPTION *</Typography>
            <TextField
              fullWidth
              multiline
              rows={4}
              value={formData.description}
              onChange={(e) => handleChange("description", e.target.value)}
              error={Boolean(errors.description)}
              helperText={errors.description || `${formData.description.length} / 2000`}
              slotProps={{
                htmlInput: { maxLength: 2000 },
              }}
            />
          </Box>

          <Box sx={{ mb: 2.5 }}>
            <Typography sx={labelStyle}>PARKING INFO</Typography>
            <TextField
              fullWidth
              size="small"
              value={formData.parkingInfo}
              onChange={(e) => handleChange("parkingInfo", e.target.value)}
            />
          </Box>

          <Box sx={{ mb: 2.5 }}>
            <Typography sx={labelStyle}>ACCESSIBILITY INFO</Typography>
            <TextField
              fullWidth
              size="small"
              value={formData.accessibilityInfo}
              onChange={(e) =>
                handleChange("accessibilityInfo", e.target.value)
              }
            />
          </Box>

          <Box sx={{ mb: 2.5 }}>
            <Typography sx={labelStyle}>IMAGE URL</Typography>
            <TextField
              fullWidth
              size="small"
              value={formData.imageUrl}
              onChange={(e) => handleChange("imageUrl", e.target.value)}
            />
          </Box>

          <Box
            sx={{
              display: "flex",
              justifyContent: "flex-end",
              gap: 1.5,
              mt: 4,
              pt: 2,
              borderTop: "1px solid #E5E5E5",
            }}
          >
            <Button
              onClick={handleClose}
              disabled={isSaving}
              sx={{
                bgcolor: "#E9EDF3",
                color: "#0F1E3D",
                textTransform: "none",
                fontWeight: 600,
                px: 3,
              }}
            >
              Cancel
            </Button>

            <Button
              variant="contained"
              onClick={handleSubmit}
              disabled={isSaving}
              sx={{
                bgcolor: "#D4A017",
                textTransform: "none",
                fontWeight: 600,
                px: 3,
                "&:hover": {
                  bgcolor: "#B8860B",
                },
              }}
            >
              {isSaving ? "Saving..." : "Save Event"}
            </Button>
          </Box>
        </DialogContent>
      </Dialog>
    </LocalizationProvider>
  );
};

const labelStyle = {
  fontSize: "12px",
  fontWeight: 700,
  color: "#1B1B1B",
  mb: 0.8,
};

export default CreateEditEventModal;
